import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, Globe2, Users, BookOpen, Soup, Sparkles } from "lucide-react";

const BELIEFS = [
  { icon: Heart,    label: "Food is emotional" },
  { icon: Globe2,   label: "Food is cultural" },
  { icon: Sparkles, label: "Food is personal" },
  { icon: BookOpen, label: "Food is memory" },
  { icon: Soup,     label: "Food is comfort" },
  { icon: Users,    label: "Food is connection" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
        <div className="overline mb-5">About YUMO</div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95]">
          About <span className="italic text-primary">YUMO</span>
        </h1>
        <p className="mt-6 font-serif text-xl md:text-2xl tracking-tight text-foreground/85 max-w-3xl leading-snug" data-testid="about-subheadline">
          Built by food lovers. Designed for better food decisions.
        </p>
      </section>

      {/* MAIN STORY */}
      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 space-y-6 text-base md:text-lg leading-relaxed text-foreground/85" data-testid="about-story">
          <p>
            We're a bunch of foodies who believe food is more than calories.
          </p>
          <p className="font-serif text-xl md:text-2xl italic text-foreground leading-snug">
            Food creates memories. Food creates conversations. Food creates celebrations.
          </p>
          <p>
            Food creates comfort during difficult days. Food connects families, friends, cultures, and generations.
          </p>
          <p>
            We started YUMO because choosing what to eat has become surprisingly difficult. People spend countless hours scrolling through videos, menus, recipes, and reviews — still not knowing what they truly want.
          </p>
          <p>
            We thought there should be a better way. So we started building YUMO.
          </p>
          <p className="text-foreground font-medium">
            A Personal Food Intelligence Companion designed to understand not only what people eat, but why they eat it.
          </p>
        </div>
      </section>

      {/* OUR BELIEF */}
      <section className="border-t border-border bg-foreground text-background">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="about-belief">
          <div className="overline text-background/60 mb-5">Our Belief</div>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
            We believe…
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {BELIEFS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3" data-testid={`belief-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
                <span className="font-serif text-2xl tracking-tight">{label}.</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16" data-testid="mission-vision">
          <div>
            <div className="overline mb-4 text-primary">Our Mission</div>
            <h3 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              To help people make happier, simpler, and more meaningful food decisions.
            </h3>
          </div>
          <div>
            <div className="overline mb-4 text-primary">Our Vision</div>
            <h3 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              To become the world's most trusted Food Intelligence Operating System — a place where technology helps people reconnect with the joy of eating.
            </h3>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center" data-testid="about-closing">
          <p className="font-serif text-2xl md:text-3xl leading-snug tracking-tight text-foreground/90">
            Whether you're celebrating something special, missing home, feeding your family, exploring a new cuisine, or simply wondering what to eat tonight…
          </p>
          <p className="mt-6 font-serif text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            YUMO is here to help.
          </p>
          <p className="mt-6 text-base md:text-lg text-muted-foreground italic">
            Because sometimes one delicious meal is all it takes to change your mood.
          </p>
          <Link to="/signup">
            <Button size="lg" className="mt-10 rounded-full h-12 px-7" data-testid="about-cta">
              Start My Food Journey <ArrowRight className="ml-1 w-4 h-4" strokeWidth={1.75} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
